import { Link2 } from 'lucide-react';

import type { CompanyColor } from '@ottd/api';

import { PlayerAvatar } from '@/components/prototype/player-avatar';
import { PresidentFace } from '@/components/prototype/president-face';
import { cn } from '@/lib/utils';

/*
 * The player and the company they ran, tiled side by side and chained together
 * over the seam - "this person ran that company". The president portrait's
 * background is the in-game company colour, so the pair carries the company's
 * identity without spending a line of text on it.
 *
 * A company nobody on the site claimed keeps an empty socket where the avatar
 * would be: nothing to chain it to, and rows stay aligned either way.
 *
 * The game screen's own company card does not use this - at card size the
 * player's avatar leads at 64px with the portrait as a corner badge.
 */

/*
 * Both tiles are the same height (26 x 1.293 rounds to the avatar's 34), so
 * the pair never sets the row height on its own.
 */
const AVATAR_PX = 34;

const FACE_PX = 26;

const CHIP_PX = 16;

const GLYPH_PX = 10;

/** How far the chip overlaps each tile, pulling the two together. */
const SEAM_PULL_PX = 6;

interface IdentityPairProps {
  /** The player who ran the company; null when nobody on the site claimed it. */
  player: string | null;
  /** Uploaded avatar URL; null → the name identicon. */
  avatar: string | null;
  company: string;
  color: CompanyColor;
  className?: string;
}

export function IdentityPair({ player, avatar, company, color, className }: IdentityPairProps) {
  return (
    <span className={cn('flex shrink-0 items-center', className)}>
      {player !== null ? (
        <PlayerAvatar name={player} src={avatar} size={AVATAR_PX} decorative />
      ) : (
        <span
          className="bevel-in bg-bar-bg block"
          style={{ width: AVATAR_PX, height: AVATAR_PX }}
          aria-hidden
        />
      )}
      {/* Negative margins pull the tiles together so the chip straddles the
          seam; it paints over the portrait only with a stacking context. */}
      <span
        className={cn(
          'relative z-10 flex items-center justify-center',
          player !== null && 'bevel bg-btn',
        )}
        style={{ width: CHIP_PX, height: CHIP_PX, marginInline: -SEAM_PULL_PX }}
        aria-hidden
        data-testid={player !== null ? 'identity-chain' : undefined}
      >
        {player !== null ? (
          <Link2
            className="text-strong"
            style={{ width: GLYPH_PX, height: GLYPH_PX }}
            strokeWidth={3}
          />
        ) : null}
      </span>
      <PresidentFace color={color} name={company} width={FACE_PX} strip={false} />
    </span>
  );
}
